import { User } from '@supabase/supabase-js';

/**
 * 用户类型枚举
 */
export type UserType = 'guest' | 'local' | 'registered';

/**
 * 获取用户类型
 * @param user Supabase 用户对象
 * @returns 用户类型
 */
export const getUserType = (user: User | null): UserType => {
  if (!user) return 'guest';
  if (user.is_anonymous) return 'local';
  return 'registered';
};

/**
 * 检查是否为本地用户（匿名用户）
 * @param user Supabase 用户对象
 * @returns 是否为本地用户
 */
export const isLocalUser = (user: User | null): boolean => {
  return user?.is_anonymous === true;
};

/**
 * 检查是否为已注册用户
 * @param user Supabase 用户对象
 * @returns 是否为已注册用户
 */
export const isRegisteredUser = (user: User | null): boolean => {
  return user !== null && !user.is_anonymous;
};

/**
 * 检查用户是否可以绑定账号
 * @param user Supabase 用户对象
 * @returns 是否可以绑定账号
 */
export const canBindAccounts = (user: User | null): boolean => {
  return user !== null; // 本地用户和正式用户都可以绑定
};

/**
 * 获取用户显示名称
 * @param user Supabase 用户对象
 * @returns 用户显示名称
 */
export const getUserDisplayName = (user: User | null): string => {
  if (!user) return '访客';
  
  if (user.is_anonymous) {
    return '本地用户';
  }
  
  // 优先使用 user_metadata 中的 full_name
  if (user.user_metadata?.full_name) {
    return user.user_metadata.full_name;
  }
  
  // 其次使用邮箱的用户名部分
  if (user.email) {
    return user.email.split('@')[0];
  }
  
  return '用户';
};

/**
 * 获取用户显示邮箱
 * @param user Supabase 用户对象
 * @returns 用户显示邮箱
 */
export const getUserDisplayEmail = (user: User | null): string => {
  if (!user) return '';
  
  if (user.is_anonymous) {
    return 'local@device';
  }
  
  return user.email || '';
};

/**
 * 检查用户是否需要升级账号
 * @param user Supabase 用户对象
 * @returns 是否需要升级账号
 */
export const shouldShowAccountUpgrade = (user: User | null): boolean => {
  return isLocalUser(user);
};

/**
 * 检查用户是否可以管理多个账号
 * @param user Supabase 用户对象
 * @returns 是否可以管理多个账号
 */
export const canManageMultipleAccounts = (user: User | null): boolean => {
  return isRegisteredUser(user);
};

/**
 * 获取用户头像 URL
 * @param user Supabase 用户对象
 * @returns 头像 URL 或 null
 */
export const getUserAvatarUrl = (user: User | null): string | null => {
  if (!user) return null;
  
  // 匿名用户没有头像
  if (user.is_anonymous) return null;
  
  return user.user_metadata?.avatar_url || null;
};

/**
 * 获取用户的身份提供商列表
 * @param user Supabase 用户对象
 * @returns 身份提供商列表
 */
export const getUserIdentityProviders = (user: User | null): string[] => {
  if (!user || user.is_anonymous) return [];
  
  // 从 identities 中提取提供商信息
  return user.identities?.map(identity => identity.provider) || [];
};
